/**
 * LandingPage V3 - World-Class Front Page
 * 
 * Iteration 3: Added noise overlay, secondary CTA, mobile optimizations.
 * Pleasance.app - "Play Without Shame"
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Book, Sparkles, MessageCircle, ChevronRight, Shield, ChevronUp, Award, Heart, User } from 'lucide-react';
import CrisisResources from '../components/CrisisResources';

// Animated counter component
const Counter = ({ target, duration = 2000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let start = 0;
        const end = parseInt(target);
        const increment = end / (duration / 16);
        const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
                setCount(end);
                clearInterval(timer);
            } else {
                setCount(Math.floor(start));
            }
        }, 16);
        return () => clearInterval(timer);
    }, [target, duration]);

    return <span className="tabular-nums" aria-live="polite" aria-atomic="true">{count.toLocaleString()}</span>;
};

function LandingPage() {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
        document.title = "Pleasance — Where Desire Meets Clarity";

        // V3: Add meta description for SEO
        const metaDesc = document.querySelector('meta[name="description"]');
        if (!metaDesc) {
            const meta = document.createElement('meta');
            meta.name = 'description';
            meta.content = 'Pleasance is the pleasure education platform for curious adults. 500+ guides on intimacy, desire, and communication. Zero judgment. Zero tracking.';
            document.head.appendChild(meta);
        }
    }, []);

    return (
        <div className="min-h-screen bg-charcoal text-cream font-sans overflow-x-hidden scroll-smooth">

            {/* V3: Noise Overlay for V9 aesthetic parity */}
            <div
                className="fixed inset-0 pointer-events-none z-[1000] mix-blend-overlay opacity-[0.04]"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)'/%3E%3C/svg%3E")`
                }}
            />

            {/* Corner Decorations - V10 Brand Elements - Increased opacity per V2 */}
            <div className="fixed top-0 left-0 w-28 h-28 opacity-20 z-10 pointer-events-none">
                <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0,0 L120,0 L120,20 L20,20 L20,120 L0,120 Z" fill="#E76F81" />
                </svg>
            </div>
            <div className="fixed bottom-0 right-0 w-28 h-28 opacity-20 z-10 pointer-events-none">
                <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M120,120 L0,120 L0,100 L100,100 L100,0 L120,0 Z" fill="#F4A261" />
                </svg>
            </div>

            {/* HERO SECTION */}
            <section className="relative min-h-[85vh] lg:min-h-[90vh] flex flex-col lg:grid lg:grid-cols-[55%_45%] xl:grid-cols-[50%_50%] gap-8 lg:gap-12 bg-[#0A0A0A] overflow-hidden">
                {/* Subtle plum vignettes */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_0%_0%,rgba(94,33,41,0.2),transparent_50%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_100%_100%,rgba(74,26,31,0.15),transparent_50%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(196,163,90,0.03),transparent_40%)]" />

                {/* Corner Flourishes */}
                <div className="absolute top-4 left-4 w-16 h-16 border-l-2 border-t-2 border-[#C4A35A]/20" />
                <div className="absolute top-4 right-4 w-16 h-16 border-r-2 border-t-2 border-[#C4A35A]/20" />
                <div className="absolute bottom-4 left-4 w-16 h-16 border-l-2 border-b-2 border-[#C4A35A]/20" />
                <div className="absolute bottom-4 right-4 w-16 h-16 border-r-2 border-b-2 border-[#C4A35A]/20" />

                <div className={`relative z-10 flex flex-col justify-center px-6 md:px-12 lg:pl-16 xl:pl-20 lg:pr-0 py-16 md:py-20 lg:py-24 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

                    {/* Decorative Header Element */}
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-[#C4A35A]/60" />
                        <span className="text-[#C4A35A]/70 text-xs font-bold uppercase tracking-[0.3em] font-fantasy">The Archive</span>
                        <div className="w-8 h-[1px] bg-gradient-to-l from-transparent to-[#C4A35A]/60" />
                    </div>

                    {/* Main Headline - Fantasy Typography */}
                    <h1 className="font-fantasy text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold leading-tight tracking-wide mb-6">
                        <span className="text-cream/85 block">The Encyclopedia of</span>
                        <em className="not-italic bg-gradient-to-r from-[#C4A35A] via-wine-400 to-[#C4A35A] bg-clip-text text-transparent">
                            Human Pleasure
                        </em>
                    </h1>

                    {/* Gold Divider */}
                    <div className="ornate-divider w-48 mb-8" />

                    {/* Value prop */}
                    <p className="text-lg md:text-xl text-cream/50 font-medium mb-10 max-w-lg leading-relaxed">
                        1,000+ Verified Guides. <span className="text-[#C4A35A] font-semibold">Zero Judgment</span>. All Tracking Optional.
                    </p>

                    {/* Hero CTA - Fantasy Button */}
                    <Link
                        to="/browse"
                        className="btn-fantasy group inline-flex items-center gap-3 w-fit text-base md:text-lg"
                    >
                        Begin Your Journey
                        <ChevronRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform" />
                    </Link>

                    {/* Secondary CTA */}
                    <div className="mt-8">
                        <a href="#features" className="text-wine-400/60 hover:text-[#C4A35A] text-sm font-medium transition-colors duration-300 flex items-center gap-2">
                            <span className="w-4 h-[1px] bg-current" />
                            Methodology & Sources
                        </a>
                    </div>
                </div>

                {/* Right: Visual Panel - Magic Book Theme */}
                <div className="relative hidden lg:flex items-center justify-center">
                    <div className="absolute top-16 left-0 w-full h-[500px] xl:h-[600px] bg-gradient-to-br from-wine-900 to-[#1A0808] shadow-2xl shadow-black/80 overflow-hidden">
                        {/* Ambient Glow Layers */}
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(94,33,41,0.4),transparent_50%)]" />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(42,157,143,0.25),transparent_45%)]" /> {/* Magic glow */}

                        {/* Celtic Border Pattern */}
                        <div className="absolute inset-4 border border-[#C4A35A]/15" />
                        <div className="absolute inset-8 border border-[#C4A35A]/10" />

                        {/* Decorative Circles */}
                        <div className="absolute top-20 left-20 w-32 h-32 border border-wine-600/30 rounded-full" />
                        <div className="absolute bottom-24 right-16 w-48 h-48 border border-[#C4A35A]/20 rounded-full" />

                        {/* Central Magic Symbol */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-teal-600/20 to-wine-600/20 blur-2xl" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-16 h-16 border-2 border-[#C4A35A]/30 rounded-full flex items-center justify-center">
                                    <div className="w-8 h-8 border border-teal-500/40 rounded-full magic-glow" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </section>

            {/* STATEMENT SECTION */}
            <section className="relative bg-gradient-to-b from-wine-950 to-wine-900 py-24 md:py-32 text-center overflow-hidden">
                {/* Ambient Glow */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_50%,rgba(94,33,41,0.2),transparent_70%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_88%_50%,rgba(42,157,143,0.15),transparent_65%)]" />

                <div className="relative z-10 max-w-4xl mx-auto px-6">
                    <p className="font-fantasy text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium leading-tight mb-10 text-cream/80">
                        <span className="text-[#C4A35A] font-bold">1,000+ in-depth guides</span> to help you explore safely and communicate clearly.
                    </p>
                    <Link
                        to="/browse"
                        className="btn-fantasy inline-block"
                    >
                        Explore the Database
                    </Link>
                </div>
            </section>

            {/* TESTIMONIAL SECTION - Anonymous */}
            <section className="bg-[#0A0A0A] py-24 md:py-32 border-y border-wine-700/20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                        {/* Quote 1 - Deep Information */}
                        <blockquote className="bg-wine-950/30 border border-wine-700/30 rounded-xl p-8 text-center">
                            <p className="text-cream/70 italic mb-6 leading-relaxed">
                                "Each entry has safety protocols, psychological context, and real risk assessments. This is the depth I couldn't find anywhere else."
                            </p>
                            <footer className="text-sm">
                                <span className="text-cream/40">— Anonymous User</span>
                            </footer>
                        </blockquote>
                        {/* Quote 2 - Profile System */}
                        <blockquote className="bg-wine-950/30 border border-wine-700/30 rounded-xl p-8 text-center">
                            <p className="text-cream/70 italic mb-6 leading-relaxed">
                                "The profile matching helped me articulate what I actually wanted—and find partners who understood my limits from day one."
                            </p>
                            <footer className="text-sm">
                                <span className="text-cream/40">— Anonymous User</span>
                            </footer>
                        </blockquote>
                        {/* Quote 3 - Comprehensive Coverage */}
                        <blockquote className="bg-wine-950/30 border border-wine-700/30 rounded-xl p-8 text-center">
                            <p className="text-cream/70 italic mb-6 leading-relaxed">
                                "Psychology, technique, negotiation scripts, aftercare protocols—everything I needed to explore safely was in one place."
                            </p>
                            <footer className="text-sm">
                                <span className="text-cream/40">— Anonymous User</span>
                            </footer>
                        </blockquote>
                    </div>
                </div>
            </section>

            {/* FEATURE CARDS SECTION */}
            <section id="features" className="bg-charcoal py-20 md:py-32 lg:py-48">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-20 md:space-y-32 lg:space-y-48">

                    {/* Card 1: Encyclopedia */}
                    <article className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-8 md:gap-12 lg:gap-24 items-center">
                        <div>
                            <div className="w-16 md:w-24 h-16 md:h-24 mb-6 md:mb-8">
                                <Book className="w-full h-full text-wine-500" strokeWidth={1.5} />
                            </div>
                            <span className="text-wine-500 text-xs font-black uppercase tracking-[3px] mb-3 md:mb-4 block">The Core</span>
                            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 md:mb-6 leading-[0.95]">Verified Knowledge</h2>
                            <p className="text-lg md:text-xl text-cream/70 leading-relaxed mb-6 md:mb-8 max-w-xl">
                                Desire is often shrouded in misinformation. We strip away the stigma to provide clear, actionable, and safety-focused information on over 1,000 topics.
                            </p>
                            <Link to="/browse" className="inline-block border-2 border-wine-500 text-wine-500 px-8 py-4 text-sm font-black uppercase tracking-widest hover:bg-gradient-to-r hover:from-wine-600 hover:to-teal-600 hover:text-white hover:border-transparent transition-all duration-300">
                                Enter the Archives
                            </Link>
                        </div>
                        <div className="h-80 lg:h-[400px] bg-gradient-to-br from-charcoal-900 to-wine-950 shadow-2xl relative overflow-hidden">
                            <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(244,162,97,0.18)_1.5px,transparent_1.5px)] bg-[length:22px_22px]" />
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_65%_45%,rgba(231,111,129,0.14),transparent_70%)]" />
                            {/* V6: Visual book stack mockup */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="relative">
                                    {[0, 1, 2, 3, 4].map((i) => (
                                        <div
                                            key={i}
                                            className="absolute w-32 h-44 rounded-sm shadow-lg"
                                            style={{
                                                background: `linear-gradient(135deg, ${['#E76F81', '#0369a1', '#F4A261', '#4A3D8F', '#64748B'][i]} 0%, ${['#C73E52', '#082f49', '#D97706', '#1A0F2E', '#475569'][i]} 100%)`,
                                                transform: `translateX(${i * 12}px) translateY(${i * -4}px) rotate(${-5 + i * 2}deg)`,
                                                zIndex: 5 - i,
                                            }}
                                        >
                                            <div className="absolute left-2 top-4 bottom-4 w-1 bg-white/20 rounded" />
                                            <div className="absolute bottom-3 left-3 right-3 text-white/40 text-[8px] font-bold">
                                                {['001', '042', '108', '256', '500'][i]}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </article>

                    {/* Card 2: The Translation Layer (Formerly Conversation Tools) */}
                    <article className="bg-gradient-to-r from-teal-600/5 to-wine-600/5 border-l-4 border-teal-500 p-6 sm:p-8 md:p-12 lg:p-20 text-center relative overflow-hidden">
                        {/* V7: Background visual - chat bubbles */}
                        <div className="absolute inset-0 opacity-10 pointer-events-none hidden md:block">
                            <div className="absolute top-8 left-[10%] w-32 h-12 bg-teal-500/30 rounded-2xl rounded-bl-none" />
                            <div className="absolute top-24 left-[15%] w-48 h-10 bg-teal-500/20 rounded-2xl rounded-bl-none" />
                            <div className="absolute top-16 right-[12%] w-36 h-14 bg-wine-500/30 rounded-2xl rounded-br-none" />
                            <div className="absolute bottom-20 right-[18%] w-28 h-10 bg-wine-500/20 rounded-2xl rounded-br-none" />
                            <div className="absolute bottom-8 left-[20%] w-40 h-12 bg-amber-500/20 rounded-2xl rounded-bl-none" />
                        </div>
                        <div className="max-w-3xl mx-auto relative z-10">
                            <div className="w-16 md:w-24 h-16 md:h-24 mx-auto mb-6 md:mb-8">
                                <MessageCircle className="w-full h-full text-teal-500" strokeWidth={1.5} />
                            </div>
                            <span className="text-teal-500 text-xs font-black uppercase tracking-[3px] mb-3 md:mb-4 block">The Translation Layer</span>
                            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 md:mb-6 leading-[0.95]">Shared Language</h2>
                            <p className="text-lg md:text-xl text-cream/70 leading-relaxed mb-6 md:mb-8">
                                Miscommunication is the enemy of consent. We provide structured scripts and shared definitions to bridge the gap between "I want" and "I understand."
                            </p>
                            <button disabled className="inline-block border-2 border-teal-500/50 text-teal-500/50 px-6 md:px-8 py-3 md:py-4 text-sm font-black uppercase tracking-widest cursor-not-allowed">
                                Developing
                            </button>
                        </div>
                    </article>

                    {/* Card 3: Your Personal Profile */}
                    <article className="grid grid-cols-1 lg:grid-cols-[40%_60%] gap-8 md:gap-12 lg:gap-24 items-center">
                        <div className="h-60 md:h-80 lg:h-[400px] bg-gradient-to-br from-charcoal-900 to-wine-950 shadow-2xl relative overflow-hidden order-2 lg:order-1">
                            <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(244,162,97,0.18)_1.5px,transparent_1.5px)] bg-[length:22px_22px]" />
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_35%_55%,rgba(231,111,129,0.15),transparent_70%)]" />
                            {/* Visual profile mockup */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="relative">
                                    {/* Profile card mockup */}
                                    <div className="w-48 md:w-64 bg-charcoal-950/80 border border-wine-700/30 rounded-xl p-4 shadow-xl">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-10 h-10 rounded-full bg-wine-600/30" />
                                            <div className="flex-1">
                                                <div className="h-3 bg-cream/20 rounded w-24 mb-1" />
                                                <div className="h-2 bg-cream/10 rounded w-16" />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="h-2 bg-wine-500/30 rounded w-full" />
                                            <div className="h-2 bg-teal-500/30 rounded w-3/4" />
                                            <div className="h-2 bg-amber-500/30 rounded w-5/6" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="order-1 lg:order-2">
                            <div className="w-16 md:w-24 h-16 md:h-24 mb-6 md:mb-8">
                                <User className="w-full h-full text-wine-500" strokeWidth={1.5} />
                            </div>
                            <span className="text-wine-500 text-xs font-black uppercase tracking-[3px] mb-3 md:mb-4 block">Your Profile</span>
                            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 md:mb-6 leading-[0.95]">Personal Discovery</h2>
                            <p className="text-lg md:text-xl text-cream/70 leading-relaxed mb-6 md:mb-8 max-w-xl">
                                Build your personal interest map. Track what you've explored, save guides for later, and discover new territories based on your unique preferences.
                            </p>
                            <Link to="/browse" className="inline-flex items-center gap-2 border-2 border-wine-500 text-wine-500 px-6 md:px-8 py-3 md:py-4 text-sm font-black uppercase tracking-widest hover:bg-wine-500 hover:text-white transition-all">
                                Start Exploring
                            </Link>
                        </div>
                    </article>
                </div>
            </section >

            {/* Press section REMOVED - no placeholder press, per critique */}

            {/* Privacy Section - Conversational, not corporate */}
            <section className="bg-gradient-to-b from-charcoal-950 to-charcoal py-24 md:py-32">
                <div className="max-w-6xl mx-auto px-6">
                    <h2 className="font-display text-4xl md:text-5xl font-black text-center mb-16">
                        We don't <span className="text-wine-400">snoop</span>. Period.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="text-center p-6">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
                                <Lock className="w-8 h-8 text-emerald-400" />
                            </div>
                            <h3 className="font-bold text-lg mb-2">Your Data, Your Control</h3>
                            <p className="text-cream/50 text-sm">Encrypted by default. Accounts optional. We only store what you choose to save.</p>
                        </div>
                        <div className="text-center p-6">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-wine-500/10 flex items-center justify-center">
                                <Shield className="w-8 h-8 text-wine-400" />
                            </div>
                            <h3 className="font-bold text-lg mb-2">No Surveillance</h3>
                            <p className="text-cream/50 text-sm">No ad trackers. No data brokers. No profiling. Your browsing stays private.</p>
                        </div>
                        <div className="text-center p-6">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
                                <Sparkles className="w-8 h-8 text-amber-400" />
                            </div>
                            <h3 className="font-bold text-lg mb-2">Deep Research</h3>
                            <p className="text-cream/50 text-sm">Every guide includes psychology, safety, negotiation scripts, and aftercare protocols.</p>
                        </div>
                        <div className="text-center p-6">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-500/10 flex items-center justify-center">
                                <Book className="w-8 h-8 text-purple-400" />
                            </div>
                            <h3 className="font-bold text-lg mb-2">GDPR Compliant</h3>
                            <p className="text-cream/50 text-sm">EU-hosted, fully compliant. Request deletion anytime.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* V9: FAQ Section */}
            <section className="bg-charcoal py-24 md:py-32 border-t border-wine-900/10">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-display text-4xl md:text-5xl font-black text-center mb-16">
                        Common <span className="text-wine-400">Questions</span>
                    </h2>
                    <div className="space-y-4">
                        {[
                            { q: "Is my browsing really private?", a: "Yes. We use encryption by default and never sell your data. Accounts are optional—anonymous browsing is always available." },
                            { q: "Where does this information come from?", a: "Research, community knowledge, and asking around. We're adding professional review—but we're not waiting for permission to be useful." },
                            { q: "Can my partner see my activity?", a: "Not unless you choose to share. Your saved items and preferences stay completely private until you decide to share." },
                            { q: "What makes this different from porn sites?", a: "Not therapy. Not porn. The stuff in between. We're the guidebook to your desires—not the destination." },
                        ].map((faq, i) => (
                            <details key={i} className="group border border-wine-900/20 rounded-lg bg-charcoal-950/50">
                                <summary className="flex items-center justify-between p-6 cursor-pointer text-lg font-bold hover:text-wine-400 transition-colors">
                                    {faq.q}
                                    <ChevronRight className="w-5 h-5 transition-transform group-open:rotate-90" />
                                </summary>
                                <div className="px-6 pb-6 text-cream/60 leading-relaxed">
                                    {faq.a}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* V9: Email Waitlist Capture */}
            <section className="bg-gradient-to-b from-charcoal-950 to-wine-950/30 py-20 md:py-28 border-t border-wine-900/20">
                <div className="max-w-2xl mx-auto px-6 text-center">
                    <h2 className="font-display text-3xl md:text-4xl font-black mb-4">
                        Get Early Access
                    </h2>
                    <p className="text-cream/60 mb-8">
                        Be the first to know when new features launch. No spam, ever.
                    </p>
                    <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
                        <input
                            type="email"
                            placeholder="your@email.com"
                            className="flex-1 bg-charcoal-950 border-2 border-wine-900/30 px-6 py-4 text-cream placeholder:text-cream/30 focus:border-wine-500 focus:outline-none transition-colors rounded-none"
                        />
                        <button
                            type="submit"
                            className="bg-wine-600 text-white px-8 py-4 font-black uppercase tracking-widest hover:bg-wine-500 transition-all"
                        >
                            Notify Me
                        </button>
                    </form>
                    <p className="text-cream/30 text-xs mt-4">We won't spam you. Scout's honor.</p>
                </div>
            </section>

            {/* V10: Credibility Strip - Honest Claims Only */}
            <section className="bg-charcoal-950 py-12 border-y border-wine-900/10">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-center">
                        <div className="flex items-center gap-3 text-cream/50">
                            <Book className="w-6 h-6 text-wine-400" />
                            <span className="text-sm">500+ Guides</span>
                        </div>
                        <div className="flex items-center gap-3 text-cream/50">
                            <Shield className="w-6 h-6 text-amber-400" />
                            <span className="text-sm">No Profiling</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* V10: Back to Top Button */}
            <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="fixed bottom-8 right-8 w-12 h-12 bg-wine-600 hover:bg-wine-500 text-white rounded-full shadow-lg shadow-wine-600/30 flex items-center justify-center transition-all hover:scale-110 z-50"
                aria-label="Back to top"
            >
                <ChevronUp className="w-6 h-6" />
            </button>

            {/* FOOTER */}
            < footer className="bg-charcoal-950 border-t border-wine-900/20 py-20 md:py-32" >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
                    <div>
                        <p className="text-base md:text-lg leading-relaxed text-cream/80">
                            <strong className="text-wine-400 font-black">Pleasance</strong> is built by people who think curiosity about desire—even the taboo kind—deserves honest answers. We don't sell your data. We don't track you.
                        </p>
                    </div>
                    <div>
                        <h4 className="text-wine-400 text-xs font-black uppercase tracking-widest mb-6">Product</h4>
                        <nav className="space-y-3">
                            <Link to="/browse" className="block text-cream/60 hover:text-wine-500 hover:pl-2 transition-all">Library</Link>
                            <span className="block text-cream/40 cursor-not-allowed">Tools (Soon)</span>
                            <span className="block text-cream/40 cursor-not-allowed">Vault (Soon)</span>
                            <span className="block text-cream/40 cursor-not-allowed">Pricing</span>
                        </nav>
                    </div>
                    <div>
                        <h4 className="text-wine-400 text-xs font-black uppercase tracking-widest mb-6">Company</h4>
                        <nav className="space-y-3">
                            <Link to="/about" className="block text-cream/60 hover:text-wine-500 hover:pl-2 transition-all">About</Link>
                            <Link to="/privacy" className="block text-cream/60 hover:text-wine-500 hover:pl-2 transition-all">Privacy</Link>
                            <Link to="/terms" className="block text-cream/60 hover:text-wine-500 hover:pl-2 transition-all">Terms</Link>
                            <a href="mailto:hello@pleasance.app" className="block text-cream/60 hover:text-wine-500 hover:pl-2 transition-all">Contact</a>
                        </nav>
                    </div>
                </div>

                {/* V2: Trust Badges + 18+ Disclaimer */}
                <div className="max-w-7xl mx-auto px-6 mt-16 pt-8 border-t border-cream/10">
                    <div className="flex flex-wrap items-center justify-center gap-8 mb-8 text-cream/40 text-sm">
                        <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-wine-400" />
                            <span>End-to-End Encrypted</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-amber-500" />
                            <span>GDPR Compliant</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-wine-500" />
                            <span>No Data Sold</span>
                        </div>
                    </div>
                    <div className="text-center text-sm text-cream/40">
                        <p className="mb-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-wine-900/30 border border-wine-700/30 rounded text-wine-400 text-xs font-bold">18+</span>
                            <span className="ml-2">Adults only.</span>
                        </p>

                        {/* Crisis Resources - compact footer version */}
                        <CrisisResources compact />

                        <p className="mt-4">© 2026 Pleasance. All rights reserved.</p>
                    </div>
                </div>
            </footer >
        </div >
    );
}

export default LandingPage;
